import * as queryString from 'query-string'
import moment from 'moment'
import { notificationService } from '../services/notificationService'
import type { NotificationOptions, NotificationSeverity } from '../services/notificationService'

// Debounce function to limit resize event handling
const debounce = (func: Function, wait: number) => {
  let timeout: number
  return function executedFunction(...args: any[]) {
    const later = () => {
      clearTimeout(timeout)
      func(...args)
    }
    clearTimeout(timeout)
    timeout = window.setTimeout(later, wait)
  }
}

// Create a debounced resize handler
export const createDebouncedResizeObserver = (callback: () => void, wait: number = 100) => {
  const debouncedCallback = debounce(callback, wait)
  return new ResizeObserver(debouncedCallback)
}

export function showNoti(type: 'success' | 'error' | 'warning' | 'info', text: string) {
  showMessage(type, text)
}

export function showMessage(
  type: 'info' | 'success' | 'error' | 'warning' | 'loading',
  text: string,
  options?: NotificationOptions,
) {
  const severity: NotificationSeverity = type === 'loading' ? 'info' : type
  try {
    notificationService.show(text, severity, options)
  } catch {
    console[severity === 'error' ? 'error' : 'info'](text)
  }
}

export function isEmailValid(email: string) {
  let re =
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
  return re.test(email)
}

export function isPhoneValid(phone: string) {
  try {
    const phoneUtil = require('google-libphonenumber').PhoneNumberUtil.getInstance()
    const phoneNumber = phoneUtil.parse(phone, 'VN')
    return phoneUtil.isValidNumber(phoneNumber, 'VN')
  } catch (e) {
    return false
  }
}

/**
 * BE lưu SĐT VN không có số 0 đầu (+ countryAccessCode 84).
 * Khi hiển thị / mở form sửa, gắn lại 0 để đúng cách nhập số VN.
 */
export function toDisplayPhoneNumber(
  phone?: string | null,
  countryAccessCode?: string | null,
): string {
  if (phone == null) return ''
  const normalized = String(phone).trim()
  if (!normalized) return ''
  if (normalized.startsWith('0') || normalized.startsWith('+')) return normalized
  const code = String(countryAccessCode ?? '84').replace(/^\+/, '')
  if (code === '84') return `0${normalized}`
  return normalized
}

export function handleError(err: any) {
  if (err.message) showMessage('error', err.message + ' (' + err.code + ')')
  else {
    console.error(err)
  }
}

export function getBase64(file: any) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.readAsDataURL(file)
    reader.onload = () => resolve(reader.result)
    reader.onerror = (error) => reject(error)
  })
}

export function showPrice(price: number | undefined) {
  4
  if (price == undefined) return ""
  return Number(price).toLocaleString('en-US', { maximumFractionDigits: 6, useGrouping: true })
}

export function showPriceBK(price: number, currency?: string) {
  if (price === undefined || price === null) {
    return ''
  }
  const currencyToCode = (_curr?: string) => {
    if (_curr === 'CNY') return '¥'
    if (_curr === 'USD') return '$'
    if (_curr === null || _curr === undefined || _curr === '') return ''
    return 'đ'
  }
  return price.toLocaleString('vi', { maximumFractionDigits: 2 }) + currencyToCode(currency)
}

export function convertGender(gender: number) {
  if (gender === 1) return 'Nam'
  if (gender === 2) return 'Nữ'
  return ''
}

export const handleChangePaging = ({
  history,
  pathname,
  search,
  page,
  page_size,
}: {
  history: any
  pathname: string
  search: string
  page: number
  page_size?: number
}) => {
  const parsed: any = queryString.parse(search)
  parsed.page = page
  if (page_size) parsed.page_size = page_size
  const stringified = queryString.stringify(parsed, { skipEmptyString: true })
  history.push(pathname + '?' + stringified)
}

export function formatter(value: number | undefined) {
  return Number(value).toLocaleString('en-US', { maximumFractionDigits: 6, useGrouping: true })
}

export function formatterSoKhoi(value: number | undefined) {
  return Number(value).toLocaleString('en-US', { maximumFractionDigits: 6, useGrouping: true })
}

export const formatterNumber = (val) => {
  if (!val) return 0;
  return `${val}`.replace(/\B(?=(\d{3})+(?!\d))/g, ".").replace(/\.(?=\d{0,2}$)/g, ",");
}

export const openInNewTab = (url: string) => {
  window.open(url, '_blank', 'noopener,noreferrer');
};

export const openInTab = (url: string) => {
  window.open(url, "_self");
};

export const delay = async (value: number) => {
  await new Promise(resolve => setTimeout(resolve, value));
};

// Hàm kiểm tra quyền Clipboard
export async function requestClipboardPermission(): Promise<boolean> {
  try {
    // @ts-ignore - clipboard-write is a valid permission name but TypeScript doesn't recognize it yet
    const permissionStatus = await navigator.permissions.query({ name: "clipboard-write" });

    if (permissionStatus.state === "granted" || permissionStatus.state === "prompt") {
      console.log("Quyền clipboard đã được cấp hoặc có thể yêu cầu.");
      return true;
    } else {
      console.error("Quyền clipboard bị từ chối.");
      return false;
    }
  } catch (error) {
    console.error("Không thể kiểm tra quyền clipboard:", error);
    return false;
  }
}

// Hàm sao chép văn bản
export async function copyToClipboardWithPermission(text: string): Promise<void> {
  const hasPermission = await requestClipboardPermission();
  if (hasPermission) {
    try {
      await navigator.clipboard.writeText(text);
      console.log("Văn bản đã được sao chép thành công!");
    } catch (error) {
      console.error("Không thể sao chép văn bản:", error);
    }
  } else {
    console.error("Không có quyền truy cập Clipboard.");
  }
}

// Gọi hàm sao chép
export async function handleCopy() {
  await copyToClipboardWithPermission("Nội dung cần sao chép");
}

export const fomartDateTime = (date: string | Date) => {
  return date ? moment(date).format('DD/MM/YYYY | HH:mm') : ''
}

export const fomartDate = (date: string | Date) => {
  return date ? moment(date).format('DD/MM/YYYY') : ''
}

export const dateTimeNow = () => {
  return moment().format('DDMMYYYY_HHmmss')
}


export const isHaveValue = (value: any) => {
  return value !== undefined && value !== null && value !== '';
}